import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Avatar
} from '@mui/material';
import { Search as SearchIcon, PersonAdd as AssignIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import api from '../../services/api';

const AssignUserPackage = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [duration, setDuration] = useState(30);
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    fetchUsers();
    fetchPackages();
  }, [page, rowsPerPage]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm
        }
      });
      setUsers(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await api.get('/payments/packages');
      setPackages(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch packages');
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchUsers();
  };

  const handleOpenDialog = (user) => {
    setSelectedUser(user);
    setSelectedPackage('');
    setDialogOpen(true);
  };

  const handlePackageChange = (packageId) => {
    setSelectedPackage(packageId);
    const pkg = packages.find(p => p.id == packageId);
    if (pkg) {
      setDuration(pkg.duration_days);
      setAmount(pkg.price);
    }
  };

  const handleAssign = async () => {
    if (!selectedPackage) {
      toast.error('Please select a package');
      return;
    }

    try {
      const response = await api.post('/payments/admin-assign-package', {
        user_id: selectedUser.id,
        package_id: selectedPackage,
        duration_days: duration,
        amount: amount,
        payment_method: 'other'
      });

      if (response.data.success) {
        toast.success('Package assigned successfully');
        setDialogOpen(false);
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign package');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        Assign Package to Users
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2}>
          <TextField
            fullWidth
            label="Search User"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            sx={{
              background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)',
              whiteSpace: 'nowrap'
            }}
          >
            Search
          </Button>
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>User</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                    <Typography color="textSecondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: '#e53935' }}>
                          {user.first_name?.charAt(0) || user.phone?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="500">
                            {user.first_name || ''} {user.last_name || ''}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {user.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AssignIcon />}
                        onClick={() => handleOpenDialog(user)}
                        sx={{
                          background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
                        }}
                      >
                        Assign Package
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)', 
          color: '#fff'
        }}>
          Assign Package to {selectedUser?.first_name || selectedUser?.phone}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 3 }}>
            <InputLabel>Select Package</InputLabel>
            <Select
              value={selectedPackage}
              label="Select Package"
              onChange={(e) => handlePackageChange(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {packages.map((pkg) => (
                <MenuItem key={pkg.id} value={pkg.id}>
                  {pkg.name} - ₨{pkg.price} ({pkg.duration_days} days)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAssign} 
            variant="contained"
            disabled={!selectedPackage}
            sx={{
              background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
            }}
          >
            Assign Package
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignUserPackage;